import { Component, computed, effect, inject, signal } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogRef } from '@angular/material/dialog';
import { MatError, MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { AuthService } from '@core/services/auth.service';
import { SnackbarService } from '@core/services/snackbar.service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { DotsSpinnerComponent } from '@shared/components/dots-spinner/dots-spinner.component';
import { IconComponent } from '@shared/components/icon/icon.component';
import { MatErrorComponent } from '@shared/components/mat-error/mat-error.component';
import { ChangePasswordStore } from './change-password.store';

@Component({
  imports: [
    DotsSpinnerComponent,
    TranslateModule,
    ReactiveFormsModule,
    MatInputModule,
    MatButtonModule,
    MatFormFieldModule,
    MatErrorComponent,
    MatError,
    IconComponent,
  ],
  providers: [ChangePasswordStore],
  selector: 'app-change-password-dialog',
  templateUrl: './change-password.component.html',
})
export class ChangePasswordComponent {
  public readonly passwordForm = new FormGroup({
    confirmNewPassword: new FormControl('', [Validators.required, Validators.minLength(6)]),
    currentPassword: new FormControl('', [Validators.required, Validators.minLength(6)]),
    newPassword: new FormControl('', [Validators.required, Validators.minLength(6)]),
  });

  public readonly showCurrentPassword = signal(false);
  public readonly showNewPassword = signal(false);
  public readonly showConfirmNewPassword = signal(false);

  public readonly loading = computed(() => this.store.loading$());

  public readonly store = inject(ChangePasswordStore);
  private readonly authService = inject(AuthService);
  private readonly snackbarService = inject(SnackbarService);
  private readonly translate = inject(TranslateService);
  private readonly dialogRef = inject(MatDialogRef<ChangePasswordComponent>);

  constructor() {
    effect(() => {
      if (this.store.passwordChanged$()) {
        this.authService.reloadCurrentUser().then(() => {
          this.snackbarService.openSuccess(this.translate.instant('pages.changePassword.passwordChanged'));
          this.dialogRef.close();
        });
      }
    });

    effect(() => {
      if (this.store.incorrectCurrentPassword$()) {
        this.store.patchState({ incorrectCurrentPassword: false });
        this.passwordForm.get('currentPassword')?.setErrors({ incorrectCurrentPassword: true });
      }
    });
  }

  public onSubmit(): void {
    if (this.passwordForm.invalid) {
      this.passwordForm.markAllAsTouched();

      return;
    }

    if (this.passwordForm.value.newPassword !== this.passwordForm.value.confirmNewPassword) {
      this.passwordForm.get('confirmNewPassword')?.setErrors({ passwordsDoNotMatch: true });

      return;
    }

    this.passwordForm.get('confirmNewPassword')?.setErrors(null);

    this.store.changePassword({
      currentPassword: this.passwordForm.value.currentPassword!,
      password: this.passwordForm.value.newPassword!,
    });
  }
}
